import { ComponentProps, FC } from 'react';
import styled, { css, keyframes } from 'styled-components';

import { ReactComponent as SpinnerIcon } from '@/assets/icons/spinner.svg';
import { hexToRgba } from '@/utils/theme';

const spin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

export const RemoveButton = styled.button`
  ${({ theme }) => css`
    font-family: ${theme.typography.button.fontFamily};
    font-weight: ${theme.typography.button.fontWeight};
    color: ${theme.colors.error};
    background: ${theme.colors.errorLight};
  `}
  border-radius: 4px;
  border: solid 1px transparent;
  padding: 0 11px;
  display: inline-flex;
  height: 27px;
  align-items: center;
  font-size: 11px;
  line-height: 15px;
  transition: border-color ease-out 0.2s;
  &:hover {
    border: solid 1px;
  }
`;

export const Spinner = styled(SpinnerIcon)`
  width: 18px;
  height: 18px;
  flex-shrink: 0;
  margin-right: 8px;
  animation: ${spin} 1s linear infinite;
  circle {
    opacity: 0.25;
  }
  path {
    opacity: 0.75;
  }
`;

type ButtonType =
  | 'primary'
  | 'secondary'
  | 'action'
  | 'colorized'
  | 'red'
  | 'inverted'
  | 'danger'
  | 'reset';

interface ButtonProps {
  $active?: boolean;
  $type?: ButtonType;
  $loading: boolean;
  $small: boolean;
}

const StyledButton = styled.button<ButtonProps>`
  height: 54px;
  justify-content: center;
  transition: 0.2s ease-out all;
  display: inline-flex;
  align-items: center;
  padding: 0 40px;
  border-width: 1px;
  border-style: solid;
  border-radius: 4px;
  background: transparent;
  cursor: pointer;
  &:disabled {
    cursor: not-allowed;
  }
  ${({ theme }) => css`
    font-family: ${theme.typography.button.fontFamily};
    font-weight: ${theme.typography.button.fontWeight};
    font-size: ${theme.typography.button.fontSize};
    line-height: ${theme.typography.button.lineHeight};
  `}
  ${({ $loading }) =>
    $loading &&
    css`
      svg {
        display: none;
      }
      ${Spinner} {
        display: inline-block;
      }
    `}
  ${({ theme, $type }) =>
    $type === 'primary' &&
    css`
      color: ${theme.colors.purple};
      border-color: ${theme.colors.purple};
      &:hover {
        color: ${theme.colors.white};
        border-color: ${theme.colors.purpleLighter};
        background: ${theme.colors.purpleLighter};
      }
      &:active {
        color: ${theme.colors.white};
        border-color: ${theme.colors.purple};
        background: ${theme.colors.purple};
      }
      &:disabled {
        color: ${theme.colors.gray};
        border-color: ${theme.colors.lightGray};
        background: ${theme.colors.lightGray};
      }
    `}
    ${({ theme, $type }) =>
    $type === 'inverted' &&
    css`
      color: ${theme.colors.white};
      background: ${theme.colors.purple};
      &:hover {
        color: ${theme.colors.white};
        background: ${theme.colors.purpleLighter};
      }
      &:active {
        color: ${theme.colors.white};
        background: ${theme.colors.purple};
      }
      &:disabled {
        color: ${theme.colors.gray};
        background: ${theme.colors.lightGray};
      }
    `}
    ${({ theme, $type }) =>
    $type === 'danger' &&
    css`
      color: ${theme.colors.black};
      border-color: ${theme.colors.lightGray};
      background: transparent;
      &:hover {
        color: ${theme.colors.error};
        border-color: ${theme.colors.error};
      }
      &:active {
        color: ${theme.colors.error};
        border-color: ${theme.colors.error};
      }
      &:disabled {
        color: ${theme.colors.gray};
        background: ${theme.colors.grayscaleLight};
        &:hover {
          border-color: ${theme.colors.grayscale};
        }
      }
    `}
  ${({ theme, $type, $loading }) =>
    $type === 'primary' &&
    $loading &&
    css`
      color: ${theme.colors.white} !important;
      border-color: ${theme.colors.purple} !important;
      background: ${theme.colors.purple} !important;
    `}
  ${({ theme, $type }) =>
    $type === 'secondary' &&
    css`
      color: ${theme.colors.black};
      border-color: ${theme.colors.lightGray};
      background: ${theme.colors.white};
      &:hover {
        color: ${theme.colors.black};
        border-color: rgba(34, 34, 34, 0.25);
      }
      &:active {
        color: ${theme.colors.black};
        border-color: ${theme.colors.gray};
      }
      &:disabled {
        color: ${theme.colors.gray};
        background: ${theme.colors.grayscaleLight};
        &:hover {
          border-color: ${theme.colors.lightGray};
        }
      }
    `}
    ${({ theme, $type }) =>
    $type === 'action' &&
    css`
      color: ${theme.colors.gray};
      border-color: ${theme.colors.grayscale};
      background: ${theme.colors.white};
      &:hover,
      &:active {
        color: ${theme.colors.black};
      }
    `}
    ${({ theme, $type, $active }) =>
    $type === 'action' &&
    $active &&
    css`
      color: ${theme.colors.black} !important;
    `}
    ${({ theme, $type, $loading }) =>
    $type === 'secondary' &&
    $loading &&
    css`
      color: ${theme.colors.black} !important;
      border-color: ${theme.colors.gray} !important;
    `}
    ${({ theme, $type }) =>
    $type === 'colorized' &&
    css`
      border: 0;
      background-color: ${({ theme }) =>
        hexToRgba(theme.colors.purpleLight, 0.15)};
      height: 38px;
      font-size: 14px;
      padding: 0 20px;
      color: ${theme.colors.purple};
      &:hover,
      &:active {
        background-color: ${({ theme }) =>
          hexToRgba(theme.colors.purpleLight, 0.25)};
      }
      &:disabled {
        color: ${theme.colors.gray};
        background: ${theme.colors.grayscaleLight};
      }
      > svg {
        margin-right: 10px;
      }
    `}
    ${({ theme, $type }) =>
    $type === 'red' &&
    css`
      border-color: ${theme.colors.errorLight};
      background-color: ${({ theme }) => theme.colors.errorLight};
      color: ${theme.colors.error};

      &:hover {
        border-color: ${theme.colors.error};
      }
      &:active {
        border-color: ${theme.colors.error};
      }
      > svg {
        margin-right: 8px;
      }
      &:disabled {
        color: ${theme.colors.gray};
        border-color: ${theme.colors.lightGray};
        background: ${theme.colors.lightGray};
      }
    `}
    ${({ theme, $type }) =>
    $type === 'reset' &&
    css`
      border-color: ${theme.colors.error};
      color: ${theme.colors.error};

      &:hover,
      &:active {
        color: ${theme.colors.white};
        background-color: ${theme.colors.error};
      }
      &:disabled {
        color: ${theme.colors.gray};
        border-color: ${theme.colors.lightGray};
        background: ${theme.colors.lightGray};
      }
    `}
  ${({ $small, theme }) =>
    $small &&
    css`
      height: 32px;
      padding: 0 12px;
      font-size: ${theme.typography.smallBold.fontSize};
      line-height: ${theme.typography.smallBold.lineHeight};
      font-weight: ${theme.typography.smallBold.fontWeight};
      > svg {
        margin-right: 8px;
      }
    `}
`;

type Props = ComponentProps<typeof StyledButton> & {
  active?: boolean;
  loading?: boolean;
  small?: boolean;
  color?: ButtonType;
};

const Button: FC<Props> = ({
  children,
  active = false,
  loading = false,
  small = false,
  color = 'primary',
  ...rest
}) => {
  return (
    <StyledButton
      $active={active}
      $loading={loading}
      $small={small}
      $type={color}
      {...rest}
    >
      {loading && <Spinner />}
      {children}
    </StyledButton>
  );
};

export default Button;
